import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { GET_SONG_CONFLICTS_URL, CREATE_SONG_CONFLICT_URL, RESOLVE_SONG_CONFLICT_URL } from '../../../api/song';
import EmptyState from '../../../components/shared/EmptyState';
import { toast } from '../../../components/shared/toast';

function ConflictsTab({ song, onConflictsChange }) {
  const [conflicts, setConflicts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create Conflict form modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [copyrightConflict, setCopyrightConflict] = useState('Sound Records');
  const [conflictOwner, setConflictOwner] = useState('');
  const [conflictDate, setConflictDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Resolve Conflict modal state
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [pendingResolveId, setPendingResolveId] = useState(null);
  const [resolveDate, setResolveDate] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  const fetchConflicts = useCallback(async () => {
    if (!song?.id) return;
    setIsLoading(true);
    try {
      const data = await apiClient(GET_SONG_CONFLICTS_URL(song.id));
      if (data && data.conflicts) {
        setConflicts(data.conflicts);
        
        // Notify parent about active conflicts count
        const activeCount = data.conflicts.filter(c => c.status === 1).length;
        if (onConflictsChange) {
          onConflictsChange(activeCount > 0);
        }
      }
    } catch (err) {
      console.warn('Failed to load song conflicts:', err);
      setConflicts([]);
    } finally {
      setIsLoading(false);
    }
  }, [song?.id, onConflictsChange]);

  useEffect(() => {
    fetchConflicts();
  }, [song?.id, onConflictsChange]);

  const handleOpenCreateModal = () => {
    // Default conflict date to today in Sri Lankan timezone
    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    setConflictDate(todayStr);
    setIsCreateOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateOpen(false);
    setCopyrightConflict('Sound Records');
    setConflictOwner('');
    setConflictDate('');
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!conflictOwner.trim()) {
      toast.warning('Conflicting Owner is required.');
      return;
    }
    if (!conflictDate) {
      toast.warning('Conflict Date is required.');
      return;
    }

    setIsCreating(true);
    try {
      await apiClient(CREATE_SONG_CONFLICT_URL(song.id), {
        method: 'POST',
        body: {
          copyrightConflict,
          conflictOwner: conflictOwner.trim(),
          conflictDate
        }
      });
      toast.success('Conflict registered successfully.');
      handleCloseCreateModal();
      await fetchConflicts();
    } catch (err) {
      console.error('Failed to create conflict:', err);
      toast.error(err.message || 'Failed to register conflict.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenResolveModal = (conflictId) => {
    // Default resolve date to today in Sri Lankan timezone
    const todayStr = new Date().toLocaleDateString('en-CA');
    setResolveDate(todayStr);
    setPendingResolveId(conflictId);
    setIsResolveOpen(true);
  };

  const handleCloseResolveModal = () => {
    setIsResolveOpen(false);
    setPendingResolveId(null);
    setResolveDate('');
  };

  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    if (!resolveDate) {
      toast.warning('Resolve Date is required.');
      return;
    }

    setIsResolving(true);
    try {
      await apiClient(RESOLVE_SONG_CONFLICT_URL(song.id, pendingResolveId), {
        method: 'PATCH',
        body: { resolveDate }
      });
      toast.success('Conflict resolved successfully.');
      handleCloseResolveModal();
      await fetchConflicts();
    } catch (err) {
      console.error('Failed to resolve conflict:', err);
      toast.error(err.message || 'Failed to resolve conflict.');
    } finally {
      setIsResolving(false);
    }
  };

  const activeConflicts = conflicts.filter((c) => c.status === 1);
  const resolvedConflicts = conflicts.filter((c) => c.status === 0);

  const formatDateString = (str) => {
    if (!str) return '—';
    return str.replace(/-/g, '.');
  };

  return (
    <div className="ringtone-tab-container">
      {/* Tab Header */}
      <div className="ringtone-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '24px' }}>
        <div className="ringtone-header-left">
          <div className="ringtone-icon-red" style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="ringtone-header-text">
            <h3 className="section-title">Conflicts</h3>
            <p className="section-subtitle">Manage song copyright conflicts and resolutions</p>
          </div>
        </div>

        <button 
          className="action-btn-primary" 
          onClick={handleOpenCreateModal}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', background: 'var(--primary-color)', color: '#fff', border: 'none', fontWeight: '600', height: '40px' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>Add Conflict</span>
        </button>
      </div>

      {isLoading ? (
        // Loading placeholders
        <div className="ringtone-cards-grid">
          {[1, 2].map((i) => (
            <div key={i} className="ringtone-card" style={{ opacity: 0.6 }}>
              <div className="ringtone-card-header">
                <div className="ringtone-provider-info">
                  <div className="provider-logo" style={{ background: '#f1f5f9' }} />
                  <div style={{ width: 120, height: 14, background: '#e2e8f0', borderRadius: 4 }} />
                </div>
              </div>
              <div className="ringtone-details-list">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="ringtone-detail-row">
                    <div style={{ width: '35%', height: 10, background: '#e2e8f0', borderRadius: 3 }} />
                    <div style={{ width: '45%', height: 10, background: '#e2e8f0', borderRadius: 3 }} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : conflicts.length === 0 ? (
        <EmptyState
          title="No Conflicts Registered"
          subtitle="This song does not have any active or resolved copyright conflicts."
          hasSearch={false}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* Active Section */}
          {activeConflicts.length > 0 && (
            <div>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#dc2626', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#dc2626' }} />
                Active Conflicts ({activeConflicts.length})
              </h4>
              <div className="ringtone-cards-grid">
                {activeConflicts.map((c) => (
                  <div key={c.id} className="ringtone-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <div className="ringtone-card-header">
                      <div className="ringtone-provider-info">
                        <div className="provider-logo" style={{ color: '#ef4444', backgroundColor: '#fef2f2' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </div>
                        <h4 className="provider-name" style={{ color: '#ef4444' }}>Active Conflict</h4>
                      </div>
                    </div>

                    <div className="ringtone-details-list">
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Copyright Conflict</span>
                        <span className="rt-value text-bold">{c.copyrightConflict}</span>
                      </div>
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Conflicting Owner</span>
                        <span className="rt-value text-bold">{c.conflictOwner}</span>
                      </div>
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Conflict Date</span>
                        <span className="rt-value">{formatDateString(c.conflictDate)}</span>
                      </div>
                    </div>

                    <div className="ringtone-card-actions">
                      <button
                        className="rt-action-btn view-btn"
                        onClick={() => handleOpenResolveModal(c.id)}
                        style={{ width: '100%', justifyContent: 'center', background: '#fef2f2', color: '#ef4444', borderColor: '#fca5a5' }}
                      >
                        Resolve Conflict
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolved Section */}
          {resolvedConflicts.length > 0 && (
            <div>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#16a34a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16a34a' }} />
                Resolved Conflicts ({resolvedConflicts.length})
              </h4>
              <div className="ringtone-cards-grid">
                {resolvedConflicts.map((c) => (
                  <div key={c.id} className="ringtone-card" style={{ borderLeft: '4px solid #16a34a', opacity: 0.85 }}>
                    <div className="ringtone-card-header">
                      <div className="ringtone-provider-info">
                        <div className="provider-logo" style={{ color: '#16a34a', backgroundColor: '#f0fdf4' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                        </div>
                        <h4 className="provider-name" style={{ color: '#16a34a' }}>Resolved</h4>
                      </div>
                    </div>

                    <div className="ringtone-details-list">
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Copyright Conflict</span>
                        <span className="rt-value text-bold">{c.copyrightConflict}</span>
                      </div>
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Conflicting Owner</span>
                        <span className="rt-value text-bold">{c.conflictOwner}</span>
                      </div>
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Conflict Date</span>
                        <span className="rt-value">{formatDateString(c.conflictDate)}</span>
                      </div>
                      <div className="ringtone-detail-row">
                        <span className="rt-label">Resolve Date</span>
                        <span className="rt-value text-bold" style={{ color: '#16a34a' }}>{formatDateString(c.resolveDate)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Conflict Modal overlay */}
      {isCreateOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '400px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={handleCloseCreateModal} 
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <h3 className="modal-title-heading" style={{ marginBottom: '20px', fontSize: '1.25rem', fontWeight: '700' }}>
              Add Song Conflict
            </h3>
            
            <form onSubmit={handleCreateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  Copyright Conflict <span className="required-asterisk">*</span>
                </label>
                <select
                  className="form-select-field"
                  value={copyrightConflict}
                  onChange={(e) => setCopyrightConflict(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#fff' }}
                >
                  <option value="Sound Records">Sound Records</option>
                  <option value="Compositions">Compositions</option>
                </select>
              </div>

              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  Conflicting Owner <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="form-input-field"
                  value={conflictOwner}
                  onChange={(e) => setConflictOwner(e.target.value)}
                  placeholder="e.john doe productions"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  Conflict Date <span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  className="form-input-field"
                  value={conflictDate}
                  onChange={(e) => setConflictDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div className="modal-footer-actions" style={{ marginTop: '10px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="modal-btn modal-btn-cancel" 
                  onClick={handleCloseCreateModal}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="modal-btn modal-btn-confirm success"
                  disabled={isCreating}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: 'var(--primary-color)', color: '#fff', fontWeight: '600' }}
                >
                  {isCreating ? 'Registering...' : 'Add Conflict'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resolve Conflict Modal overlay */}
      {isResolveOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '400px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={handleCloseResolveModal} 
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            
            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div className="modal-icon-container danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.15rem', fontWeight: '700', margin: 0 }}>
                  Resolve Conflict
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', margin: '4px 0 0 0' }}>
                  Please select the resolution date to mark this conflict as resolved.
                </p>
              </div>
            </div>

            <form onSubmit={handleResolveSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-field-group">
                <label className="form-field-label" style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>
                  Resolve Date <span className="required-asterisk">*</span>
                </label>
                <input
                  type="date"
                  className="form-input-field"
                  value={resolveDate}
                  onChange={(e) => setResolveDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="modal-btn modal-btn-cancel" 
                  onClick={handleCloseResolveModal}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="modal-btn modal-btn-confirm danger"
                  disabled={isResolving}
                  style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: '600' }}
                >
                  {isResolving ? 'Resolving...' : 'Confirm Resolve'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConflictsTab;
